import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, storeToken, clearToken, isDelegated } from "@/lib/api";

const AuthCtx = createContext(null);

export const mobileEntry = () => {
  const t = localStorage.getItem("m_link_token");
  return t ? `/m/entrar/${t}` : "/login";
};

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setSession(data);
      return data;
    } catch (e) {
      // aba do contador com o acesso encerrado pelo cliente (ou sessão vencida): volta ao painel do escritório,
      // que continua logado, em vez de cair na tela de login
      if (isDelegated() && [401, 403].includes(e?.response?.status)) {
        clearToken();
        window.location.replace("/app/contabil");
        return null;
      }
      setSession(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (payload) => {
    const { data } = await api.post("/auth/login", payload);
    storeToken(data.token, payload.remember);
    setSession(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* sessão já encerrada */
    }
    clearToken();
    setSession(false);
  };

  return <AuthCtx.Provider value={{ session, setSession, refresh, login, logout }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);

export const FullLoader = () => (
  <div className="min-h-screen grid place-items-center" data-testid="full-loader">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

export function Protected({ children }) {
  const { session } = useAuth();
  const { pathname } = useLocation();
  if (session === null) return <FullLoader />;
  if (!session) return <Navigate to={pathname.startsWith("/m") ? mobileEntry() : "/login"} replace />;
  return children;
}

export function useTheme() {
  const [theme, setTheme] = useState(() => localStorage.getItem("pdv_theme") || "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("pdv_theme", theme);
  }, [theme]);
  return [theme, () => setTheme((t) => (t === "dark" ? "light" : "dark"))];
}
